/**
 * FmActionCardGrid — a grid of previewable ACTION cards.
 *
 * Replaces the giant-icon auto-cycling capability carousel for primary "what to do
 * next" surfaces. Every card is fully visible at once (no accordion / dead media
 * column): a small tokenized icon badge, a title, a one-line outcome, optional
 * preview-stat chips, and a CTA button pinned to the card foot so all cards read as
 * equal, scannable choices.
 *
 * SEALED, SCALAR API: every card field is a plain
 * string / string[] / semantic icon KEY / domain callback. NO ReactNode, children,
 * className, style, or media src crosses the boundary — the icon glyph is resolved
 * INSIDE the DS from `iconKey`, and all card chrome/geometry/spacing is DS-owned.
 */
import React from "react";
import {
  BarChartOutlined,
  CommentOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  ScanOutlined,
} from "@ant-design/icons";
import { FmButton } from "./FmButton";
import { FmChip } from "./FmChip";
import { FmConfirm, type FmActionConfirmationDescriptor } from "./FmConfirm";
import "./FmActionCardGrid.css";

/** Allowed semantic icon keys — the DS owns the glyph for each. */
export type FmActionCardIconKey =
  | "currency"
  | "clock"
  | "calendar"
  | "analysis"
  | "measurement"
  | "overview"
  | "assistant";

/** Page-neutral semantic accent owned by the card renderer. */
export type FmActionCardTone = "accent" | "teal" | "amber" | "sky" | "rose";

const ICON_REGISTRY: Record<FmActionCardIconKey, React.ReactNode> = {
  currency: <DollarOutlined />,
  clock: <ClockCircleOutlined />,
  calendar: <CalendarOutlined />,
  analysis: <FileSearchOutlined />,
  measurement: <ScanOutlined />,
  overview: <BarChartOutlined />,
  assistant: <CommentOutlined />,
};

export interface FmActionCardItem {
  key: string;
  /** Semantic icon key; the DS resolves the glyph (no ReactNode crosses the API). */
  iconKey?: FmActionCardIconKey;
  /** Semantic accent; the DS owns all color and surface decisions. */
  tone?: FmActionCardTone;
  /** Card title / action label. */
  label: string;
  /** One-line outcome describing what the action gives the owner. */
  description: string;
  /** Optional preview-stat chip labels. */
  tags?: string[];
  statusLabel?: string;
  disabledReason?: string;
  /** Call-to-action that routes to the capability. */
  cta: {
    label: string;
    variant?: "primary" | "bordered";
    onClick?: () => void;
    href?: string;
    confirmation?: FmActionConfirmationDescriptor;
  };
  "data-testid"?: string;
}

export interface FmActionCardGridProps {
  items: FmActionCardItem[];
  /** Desktop column count; collapses responsively below. Default 3. */
  columns?: 2 | 3;
  "data-testid"?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(" ");

/** Preserve order, drop duplicate tag labels so each chip has a unique value key. */
const dedupeTags = (tags: string[]): string[] => Array.from(new Set(tags));

function actionButton(item: FmActionCardItem): React.ReactElement {
  const button = (
    <FmButton
      variant={item.cta.variant ?? "primary"}
      size="md"
      href={item.cta.confirmation ? undefined : item.cta.href}
      onClick={item.cta.confirmation ? undefined : item.cta.onClick}
      disabled={Boolean(item.disabledReason)}
      // A visible verb ("Open") leans on the card title; the accessible name
      // carries both so every card's button is distinct to assistive tech.
      aria-label={
        item.cta.label === item.label ? undefined : `${item.cta.label} – ${item.label}`
      }
      data-testid={
        item["data-testid"] ? `${item["data-testid"]}-cta` : undefined
      }
    >
      {item.cta.label}
    </FmButton>
  );
  if (!item.cta.confirmation || !item.cta.onClick) return button;
  return (
    <FmConfirm
      title={item.cta.confirmation.title}
      description={item.cta.confirmation.description}
      affectedEntities={item.cta.confirmation.affectedEntities}
      confirmText={item.cta.confirmation.confirmLabel}
      cancelText={item.cta.confirmation.cancelLabel}
      onConfirm={item.cta.onClick}
      disabled={Boolean(item.disabledReason)}
      contentTestId={
        item["data-testid"] ? `${item["data-testid"]}-confirmation` : undefined
      }
    >
      {button}
    </FmConfirm>
  );
}

const FmActionCard: React.FC<{ item: FmActionCardItem }> = ({ item }) => (
  <article
    className={cx(
      "fm-action-card",
      item.tone && `fm-action-card--${item.tone}`,
    )}
    data-testid={item["data-testid"]}
  >
    {item.iconKey ? (
      <span className="fm-action-card__icon" aria-hidden="true">
        {ICON_REGISTRY[item.iconKey]}
      </span>
    ) : null}
    <h3 className="fm-action-card__title">{item.label}</h3>
    <p className="fm-action-card__description">{item.description}</p>
    {item.tags && item.tags.length > 0 ? (
      <div className="fm-action-card__tags">
        {/* Tags are distinct string labels — key by value so React reorders/reuses
            chips correctly and no array index is used. Duplicates are removed at the
            data source (the descriptor builder dedupes its highlight tags). */}
        {dedupeTags(item.tags).map((tag) => (
          <FmChip key={tag} variant="mute">
            {tag}
          </FmChip>
        ))}
      </div>
    ) : null}
    {item.statusLabel ? (
      <FmChip variant="mute">{item.statusLabel}</FmChip>
    ) : null}
    <div className="fm-action-card__cta">
      {actionButton(item)}
      {/* A disabled button cannot be hovered on touch, so its reason is printed, not tooltipped. */}
      {item.disabledReason ? (
        <p className="fm-action-card__reason">{item.disabledReason}</p>
      ) : null}
    </div>
  </article>
);

/**
 * FmActionCardGrid — equal-weight action cards laid out in a responsive grid.
 */
export const FmActionCardGrid: React.FC<FmActionCardGridProps> = ({
  items,
  columns = 3,
  "data-testid": dataTestId,
}) => {
  if (items.length === 0) return null;
  return (
    <div
      className={cx(
        "fm-action-card-grid",
        `fm-action-card-grid--cols-${columns}`,
      )}
      data-testid={dataTestId}
    >
      {items.map((item) => (
        <FmActionCard key={item.key} item={item} />
      ))}
    </div>
  );
};

FmActionCardGrid.displayName = "FmActionCardGrid";

export default FmActionCardGrid;
